import { useEffect, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Trophy, 
  Users, 
  MapPin, 
  WhatsappLogo, 
  InstagramLogo, 
  YoutubeLogo, 
  FacebookLogo, 
  TiktokLogo,
  ArrowRight,
  Star,
  Medal,
  Timer,
  X,
  EnvelopeSimple,
  Phone,
  User,
  Buildings,
  PaperPlaneTilt,
  CheckCircle,
  SpinnerGap
} from "@phosphor-icons/react";

// API Config
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants
const WHATSAPP_NUMBER = "5519978154151";
const WHATSAPP_MESSAGE = "Olá! Tenho interesse em patrocinar os eventos da Braga Sports.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/_bragasports/",
  youtube: "https://www.youtube.com/@BragaSports-y7r",
  facebook: "https://www.facebook.com/bragasports",
  tiktok: "https://www.tiktok.com/@braga.cast"
};

// Fotos Reais Braga Sports
const BRAGA_PHOTOS = {
  openTenis: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/fo555hci_IMG_2838.jpg",
  duplasTenis: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/pirh7efu_WhatsApp%20Image%202025-12-09%20at%2009.58.50.jpeg",
  trofeuBeachTennis: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/jnarjfjb_WhatsApp%20Image%202025-12-09%20at%2010.00.11.jpeg",
  timeSunset: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/kxf4afv1_WhatsApp%20Image%202025-12-09%20at%2010.03.11.jpeg",
  desafioBeachTennis: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/0ab1hmzq_WhatsApp%20Image%202025-12-09%20at%2010.07.40%20%282%29.jpeg",
  campeoesTrofeu: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/2f698eve_WhatsApp%20Image%202025-12-09%20at%2010.07.41.jpeg"
};

// Logo
const LOGO_NEGATIVO = "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/j59n7wfh_logo%20braga%20negativo.png";

// Sports Images (hero banner)
const SPORTS_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_landing-v1-preview/artifacts/e4uthrbs_imagem.png"
};

const EVENTS_2026 = [
  { month: "Maio", events: [{ name: "Torneio Inter Condomínios de Tênis", location: "Academia Vacchi Sports", type: "Tênis" }] },
  { month: "Junho", events: [
    { name: "Torneio de Vôlei na Areia e Futevôlei", location: "Condomínio Villa D'aquila", type: "Vôlei, Futevôlei" },
    { name: "Torneio Sunset na Areia", location: "Condomínio Damha I", type: "Beach Tennis, Vôlei, Futevôlei" }
  ]},
  { month: "Julho", events: [{ name: "Olimpíadas de Férias", location: "Condomínios: Alphaville, Monte Alegre, Villa D'aquila e Reserva do Engenho", type: "Multi-esportivo" }] },
  { month: "Agosto", events: [
    { name: "4º Desafio Inter Condomínios de Beach Tennis", location: "Beach One", type: "Beach Tennis" },
    { name: "Corrida", location: "Condomínio Villa D'aquila", type: "Corrida" }
  ]},
  { month: "Setembro", events: [
    { name: "2º Inter Condomínios de Tênis", location: "Academia Vacchi Sports", type: "Tênis" },
    { name: "Torneio de Beach Tennis", location: "Condomínio Monte Alegre", type: "Beach Tennis" }
  ]},
  { month: "Outubro", events: [{ name: "Dia da Criança", location: "Alphaville e Villa D'aquila", type: "Recreação" }] },
  { month: "Novembro", events: [
    { name: "3º Sunset na Areia", location: "Condomínio Damha I", type: "Beach Tennis, Vôlei, Futevôlei" },
    { name: "Torneio de Vôlei de Areia e Futevôlei", location: "Villa D'aquila", type: "Vôlei" }
  ]},
  { month: "Dezembro", events: [{ name: "Gincana Esportiva", location: "Condomínio Villa D'Aquila", type: "Multi-esportivo" }] }
];

const GENERATIONS = [
  { 
    gen: "1ª Geração", 
    period: "Anos 30",
    name: "Maria Ferraz de Toledo Braga", 
    role: "Pioneira no esporte e educação física em Piracicaba",
    desc: "Fundadora do legado esportivo da família Braga"
  },
  { 
    gen: "2ª Geração", 
    period: "Anos 50",
    name: "Rubens Braga", 
    role: "Continuidade e expansão do legado familiar",
    desc: "Pioneiro na profissionalização do esporte na região"
  },
  { 
    gen: "3ª Geração", 
    period: "Atual",
    name: "João Braga", 
    role: "Mais de 40 anos dedicados ao esporte",
    desc: "Experiência consolidada em gestão de eventos esportivos"
  },
  { 
    gen: "4ª Geração", 
    period: "Atual",
    name: "Guilherme Braga", 
    role: "Inovação e modernidade",
    desc: "Levando o legado para uma nova era digital"
  }
];

// Contact Form Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const normalizePhone = (value) => value.replace(/\D/g, "");

  const validateForm = (data) => {
    const errors = {};

    if (!data.name.trim()) {
      errors.name = "Nome é obrigatório.";
    } else if (data.name.trim().length < 2) {
      errors.name = "Informe pelo menos 2 caracteres no nome.";
    }

    if (!data.email.trim()) {
      errors.email = "E-mail é obrigatório.";
    } else if (!validateEmail(data.email)) {
      errors.email = "E-mail inválido.";
    }

    const phoneDigits = normalizePhone(data.phone);
    if (!phoneDigits) {
      errors.phone = "Telefone/WhatsApp é obrigatório.";
    } else if (phoneDigits.length < 10) {
      errors.phone = "Informe um telefone válido com DDD.";
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: "" }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setError("Corrija os campos em vermelho antes de enviar.");
      setIsSubmitting(false);
      return;
    }

    setFormErrors({});

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        interest: "Patrocínio Agenda 2026"
      });
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "Erro ao enviar mensagem. Tente novamente ou use o WhatsApp."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleBackdropClick}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        data-testid="contact-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25 }}
          className="relative w-full max-w-lg bg-braga-dark-secondary border border-braga-navy/30 rounded-2xl shadow-2xl overflow-hidden"
          data-testid="contact-modal"
        >
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-white/10">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/50 hover:text-white transition-colors"
              data-testid="contact-modal-close"
              aria-label="Fechar"
            >
              <X weight="bold" size={20} />
            </button>
            <h3 className="font-heading text-2xl font-bold text-white" data-testid="contact-modal-title">
              Quero ser Patrocinador
            </h3>
            <p className="text-white/60 text-sm mt-1">
              Preencha o formulário e entraremos em contato
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
                data-testid="contact-success"
              >
                <CheckCircle weight="fill" size={64} className="text-green-500 mx-auto mb-4" />
                <h4 className="font-heading text-xl font-semibold text-white mb-2">
                  Mensagem Enviada!
                </h4>
                <p className="text-white/60">
                  Entraremos em contato em breve.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                {/* Name */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Nome completo *
                  </label>
                  <div className="relative">
                    <User weight="bold" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      placeholder="Seu nome"
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-braga-orange/50 transition-colors"
                      data-testid="contact-input-name"
                    />
                  </div>
                  {formErrors.name && (
                    <p className="text-red-400 text-sm mt-1" data-testid="contact-error-name">
                      {formErrors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    E-mail *
                  </label>
                  <div className="relative">
                    <EnvelopeSimple weight="bold" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="seu@email.com"
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-braga-orange/50 transition-colors"
                      data-testid="contact-input-email"
                    />
                  </div>
                  {formErrors.email && (
                    <p className="text-red-400 text-sm mt-1" data-testid="contact-error-email">
                      {formErrors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Telefone / WhatsApp *
                  </label>
                  <div className="relative">
                    <Phone weight="bold" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      minLength={10}
                      placeholder="(19) 99999-9999"
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-braga-orange/50 transition-colors"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  {formErrors.phone && (
                    <p className="text-red-400 text-sm mt-1" data-testid="contact-error-phone">
                      {formErrors.phone}
                    </p>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Empresa
                  </label>
                  <div className="relative">
                    <Buildings weight="bold" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nome da empresa (opcional)"
                      className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-braga-orange/50 transition-colors"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-white/70 text-sm font-medium mb-2">
                    Mensagem
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Conte-nos sobre seu interesse em patrocínio (opcional)"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-braga-orange/50 transition-colors resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <p className="text-red-400 text-sm" data-testid="contact-error">{error}</p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <SpinnerGap weight="bold" size={20} className="animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt weight="fill" size={20} />
                      Enviar Mensagem
                    </>
                  )}
                </button>

                {/* WhatsApp alternative */}
                <div className="text-center pt-2">
                  <p className="text-white/40 text-sm mb-2">ou fale diretamente pelo</p>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#25D366] hover:underline font-medium"
                    data-testid="contact-whatsapp-link"
                  >
                    <WhatsappLogo weight="fill" size={20} />
                    WhatsApp
                  </a>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Header Component
const Header = ({ onOpenContact }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header py-3" : "py-5 bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
        <a href="/" data-testid="header-logo">
          <img 
            src={LOGO_NEGATIVO} 
            alt="BRAGA SPORTS" 
            className="h-12 sm:h-14 w-auto"
          />
        </a>
        
        <nav className="hidden md:flex items-center gap-8">
          <button 
            onClick={() => scrollToSection("sobre")} 
            className="nav-link text-white/80 hover:text-white font-medium transition-colors"
            data-testid="nav-sobre"
          >
            Sobre
          </button>
          <button 
            onClick={() => scrollToSection("agenda")} 
            className="nav-link text-white/80 hover:text-white font-medium transition-colors"
            data-testid="nav-agenda"
          >
            Agenda 2026
          </button>
          <button 
            onClick={() => scrollToSection("contato")} 
            className="nav-link text-white/80 hover:text-white font-medium transition-colors"
            data-testid="nav-contato"
          >
            Contato
          </button>
        </nav>
        
        <button 
          onClick={onOpenContact}
          className="btn-primary px-5 py-2.5 rounded-full text-white font-semibold text-sm flex items-center gap-2"
          data-testid="header-cta"
        >
          <EnvelopeSimple weight="fill" size={18} />
          <span className="hidden sm:inline">Patrocinar</span>
        </button>
      </div>
    </motion.header>
  );
};

// Hero Banner with Background Image
const HeroBanner = () => {
  return (
    <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden" data-testid="hero-banner">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${SPORTS_IMAGES.hero})` }}
      />
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-braga-dark/60 via-braga-dark/40 to-braga-dark" />
      
      {/* Content */}
      <div className="relative h-full flex items-end pb-12 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-braga-orange font-medium tracking-[0.2em] text-sm uppercase mb-4">
              Gestão de Eventos Esportivos
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              BRAGA SPORTS
            </h1>
            <p className="text-white/70 text-lg max-w-xl">
              Quase 100 anos transformando o esporte em Piracicaba através de 4 gerações de dedicação e excelência.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ onOpenContact }) => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 400], [1, 0.95]);

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden py-20" data-testid="hero-section">
      {/* Background Effects */}
      <div className="hero-gradient" />
      <div className="hero-particles" />
      
      {/* Content */}
      <motion.div 
        style={{ opacity, scale }}
        className="relative z-10 text-center px-4 sm:px-8 max-w-5xl mx-auto"
      >
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
          data-testid="hero-title"
        >
          <span className="text-white">Agenda Esportiva</span>
          <br />
          <span className="text-gradient-orange">2026</span>
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          data-testid="hero-subtitle"
        >
          Eventos esportivos exclusivos em condomínios de alto padrão
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={onOpenContact}
            className="btn-primary inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-bold text-lg glow-orange"
            data-testid="hero-cta"
          >
            <EnvelopeSimple weight="fill" size={24} />
            Quero Patrocinar
            <ArrowRight weight="bold" size={20} />
          </button>
          
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-white/80 hover:text-white font-medium border border-white/20 hover:border-white/40 transition-colors"
            data-testid="hero-whatsapp"
          >
            <WhatsappLogo weight="fill" size={22} className="text-[#25D366]" />
            WhatsApp
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Sports Gallery Component
const SportsGallery = () => {
  const galleryImages = [
    { src: BRAGA_PHOTOS.openTenis, alt: "2º Open de Tênis Todeschini" },
    { src: BRAGA_PHOTOS.duplasTenis, alt: "Duplas de Tênis" },
    { src: BRAGA_PHOTOS.trofeuBeachTennis, alt: "Troféu Intercondomínios Beach Tennis" },
    { src: BRAGA_PHOTOS.timeSunset, alt: "Time Sunset Amistã" },
    { src: BRAGA_PHOTOS.desafioBeachTennis, alt: "2º Desafio Beach Tennis Intercondomínios" },
    { src: BRAGA_PHOTOS.campeoesTrofeu, alt: "Campeões com Troféu" }
  ];

  return (
    <section className="py-12 px-4 sm:px-8 bg-braga-dark-secondary/30" data-testid="sports-gallery">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <p className="text-braga-orange font-medium tracking-[0.15em] text-sm uppercase mb-2">
            Nossos Eventos
          </p>
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-white">
            Esporte de Alto Padrão
          </h3>
        </motion.div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {galleryImages.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="relative aspect-square overflow-hidden rounded-xl group"
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={img.src} 
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section with Family Images
const AboutSection = () => {
  const bentoCards = [
    { icon: Trophy, title: "100 Anos", subtitle: "de Legado", desc: "Quase um século de história no esporte" },
    { icon: Users, title: "4 Gerações", subtitle: "de Tradição", desc: "Família comprometida com o esporte" },
    { icon: Star, title: "Alto Padrão", subtitle: "Exclusividade", desc: "Eventos em condomínios premium" },
    { icon: Medal, title: "Excelência", subtitle: "Qualidade", desc: "Organização profissional garantida" }
  ];

  return (
    <section id="sobre" className="section-padding relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-braga-orange font-medium tracking-[0.15em] text-sm uppercase mb-4">
            Sobre a BRAGA SPORTS
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6" data-testid="about-title">
            Legado de Quase 100 Anos
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Uma trajetória de excelência no esporte e educação física em Piracicaba, 
            construída através de gerações de dedicação e paixão.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {bentoCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bento-card rounded-2xl p-6 text-center"
              data-testid={`bento-card-${index}`}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-braga-orange/10 flex items-center justify-center">
                <card.icon weight="duotone" size={28} className="text-braga-orange" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white">{card.title}</h3>
              <p className="text-braga-orange font-medium text-sm mb-2">{card.subtitle}</p>
              <p className="text-white/50 text-sm">{card.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Generations Timeline with Images */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <h3 className="font-heading text-2xl font-semibold text-white text-center mb-12" data-testid="generations-title">
            História e Autoridade
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {GENERATIONS.map((gen, index) => (
              <motion.div
                key={gen.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="glass-card rounded-2xl overflow-hidden"
                data-testid={`generation-${index}`}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Image placeholder - using gradient as family photos would come from presentation */}
                  <div className="sm:w-1/3 h-32 sm:h-auto bg-gradient-to-br from-braga-navy/40 to-braga-orange/20 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-braga-dark/50 border-2 border-braga-orange/30 flex items-center justify-center">
                      <span className="font-heading text-2xl font-bold text-braga-orange">
                        {gen.gen.charAt(0)}ª
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5 sm:w-2/3">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-3 py-1 bg-braga-orange/20 rounded-full text-braga-orange text-xs font-semibold">
                        {gen.gen}
                      </span>
                      <span className="text-white/40 text-xs">{gen.period}</span>
                    </div>
                    <h4 className="font-heading text-lg font-semibold text-white mb-1">
                      {gen.name}
                    </h4>
                    <p className="text-braga-orange text-sm font-medium mb-2">{gen.role}</p>
                    <p className="text-white/50 text-sm">{gen.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Agenda Section with Sports Images
const AgendaSection = () => {
  return (
    <section id="agenda" className="section-padding relative bg-braga-dark-secondary/50" data-testid="agenda-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-braga-orange font-medium tracking-[0.15em] text-sm uppercase mb-4">
            Calendário Exclusivo
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6" data-testid="agenda-title">
            Agenda de Eventos 2026
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Descubra as oportunidades de patrocínio ao longo do ano
          </p>
        </motion.div>

        {/* Events List */}
        <div className="space-y-3">
          {EVENTS_2026.map((monthData, monthIndex) => (
            <motion.div
              key={monthData.month}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: monthIndex * 0.05 }}
            >
              {monthData.events.map((event, eventIndex) => (
                <div
                  key={`${monthData.month}-${eventIndex}`}
                  className="group relative rounded-xl mb-3 overflow-hidden transition-all duration-300 hover:scale-[1.01]"
                  data-testid={`event-${monthData.month.toLowerCase()}-${eventIndex}`}
                >
                  {/* Orange gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-braga-orange/20 via-braga-orange/10 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Left accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-braga-orange to-braga-orange/50" />
                  
                  {/* Content */}
                  <div className="relative p-5 pl-6 flex flex-col sm:flex-row sm:items-center gap-4 bg-braga-dark-tertiary/80 border border-braga-orange/20 rounded-xl">
                    {/* Month Badge */}
                    <div className="sm:w-28 flex-shrink-0">
                      <span className="inline-block px-4 py-2 rounded-lg bg-braga-orange text-white font-heading font-bold text-sm shadow-lg shadow-braga-orange/20">
                        {monthData.month}
                      </span>
                    </div>
                    
                    {/* Event Info */}
                    <div className="flex-grow">
                      <h4 className="font-heading text-lg font-semibold text-white mb-1 group-hover:text-braga-orange transition-colors">
                        {event.name}
                      </h4>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <MapPin weight="fill" size={14} className="text-braga-orange" />
                        {event.location}
                      </div>
                    </div>
                    
                    {/* Type Tag */}
                    <div className="sm:w-36 flex-shrink-0 sm:text-right">
                      <span className="inline-block px-4 py-1.5 bg-braga-orange/20 border border-braga-orange/30 rounded-full text-braga-orange text-xs font-semibold">
                        {event.type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section (inline form)
const ContactSection = ({ onOpenContact }) => {
  return (
    <section id="contato" className="section-padding relative overflow-hidden" data-testid="contact-section">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-braga-orange/5 to-transparent" />
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto text-center relative z-10"
      >
        <div className="cta-glow glass-card rounded-3xl p-10 sm:p-16">
          <Timer weight="duotone" size={48} className="text-braga-orange mx-auto mb-6" />
          
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="contact-title">
            Faça parte deste legado
          </h2>
          
          <p className="text-white/60 text-lg mb-8 max-w-lg mx-auto">
            Associe sua marca a eventos esportivos de alto padrão e alcance um público 
            qualificado em condomínios premium.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onOpenContact}
              className="btn-primary inline-flex items-center gap-3 px-10 py-5 rounded-full text-white font-bold text-lg"
              data-testid="contact-cta-form"
            >
              <EnvelopeSimple weight="fill" size={24} />
              Quero ser patrocinador
              <ArrowRight weight="bold" size={20} />
            </button>
            
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-5 rounded-full text-white font-bold text-lg bg-[#25D366] hover:bg-[#20BD5A] transition-colors"
              data-testid="contact-cta-whatsapp"
            >
              <WhatsappLogo weight="fill" size={24} />
              WhatsApp
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-braga-dark border-t border-braga-navy/20 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Logo & Description */}
          <div>
            <img 
              src={LOGO_NEGATIVO} 
              alt="BRAGA SPORTS" 
              className="h-14 w-auto mb-4"
              data-testid="footer-logo"
            />
            <p className="text-white/50 text-sm leading-relaxed">
              Gestão de Eventos Esportivos<br />
              Quase 100 anos de tradição em Piracicaba
            </p>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-4">Contato</h4>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-white/70 hover:text-braga-orange transition-colors group"
              data-testid="footer-whatsapp"
            >
              <WhatsappLogo weight="fill" size={24} className="text-braga-orange" />
              <span className="group-hover:underline">+55 19 97815-4151</span>
            </a>
          </div>
          
          {/* Social */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-4">Redes Sociais</h4>
            <div className="flex items-center gap-4">
              <a 
                href={SOCIAL_LINKS.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-white/60 hover:text-braga-orange"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <InstagramLogo weight="fill" size={28} />
              </a>
              <a 
                href={SOCIAL_LINKS.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-white/60 hover:text-braga-orange"
                data-testid="social-youtube"
                aria-label="YouTube"
              >
                <YoutubeLogo weight="fill" size={28} />
              </a>
              <a 
                href={SOCIAL_LINKS.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-white/60 hover:text-braga-orange"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <FacebookLogo weight="fill" size={28} />
              </a>
              <a 
                href={SOCIAL_LINKS.tiktok} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-white/60 hover:text-braga-orange"
                data-testid="social-tiktok"
                aria-label="TikTok"
              >
                <TiktokLogo weight="fill" size={28} />
              </a>
            </div>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="pt-8 border-t border-white/10 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} BRAGA SPORTS. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <motion.a
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0 }}
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="floating-cta w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-110 transition-transform md:hidden"
      data-testid="floating-cta"
      aria-label="WhatsApp"
    >
      <WhatsappLogo weight="fill" size={28} className="text-white" />
    </motion.a>
  );
};

// Main App
function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <div className="App bg-braga-dark min-h-screen">
      <Header onOpenContact={() => setIsContactOpen(true)} />
      <main>
        <HeroBanner />
        <HeroSection onOpenContact={() => setIsContactOpen(true)} />
        <SportsGallery />
        <AboutSection />
        <AgendaSection />
        <ContactSection onOpenContact={() => setIsContactOpen(true)} />
      </main>
      <Footer />
      <FloatingCTA />
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </div>
  );
}

export default App;
